import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Lifebuoy, SearchStatus } from '@/components/icons';
import {
    EmptyState,
    Heading,
    PageHeader,
    PageSection,
    PageShell,
    SearchField,
    Text,
} from '@/components/system';
import { SupportPage } from '@/pages/constants';
import { createPageUrl } from '@/utils';
import { searchHelpArticles } from './articles';
import { HelpShortcutGrid } from './components/HelpShortcutGrid';
import { HelpTopicGrid } from './components/HelpTopicGrid';
import { createHelpShortcuts, HELP_TOPICS } from './data/help-content';

export default function HelpPage() {
    const supportUrl = createPageUrl(SupportPage);
    const shortcuts = createHelpShortcuts(supportUrl);
    const [query, setQuery] = useState('');

    const results = useMemo(() => searchHelpArticles(query), [query]);
    const isSearching = query.trim().length > 0;

    return (
        <PageShell width="wide">
            <PageHeader
                title="Central de ajuda"
                icon={Lifebuoy}
                meta={
                    isSearching ? (
                        <span aria-live="polite">
                            {results.length} guia{results.length === 1 ? '' : 's'} para &quot;
                            {query.trim()}&quot;
                        </span>
                    ) : undefined
                }
                toolbar={{
                    search: (
                        <SearchField
                            value={query}
                            onValueChange={setQuery}
                            placeholder="Buscar guias..."
                            aria-label="Buscar guias da central de ajuda"
                            className="w-full"
                        />
                    ),
                }}
            />

            <HelpShortcutGrid shortcuts={shortcuts} />

            <PageSection contentClassName="p-5 sm:p-6">
                {!isSearching ? (
                    <HelpTopicGrid topics={HELP_TOPICS} />
                ) : results.length === 0 ? (
                    <EmptyState
                        icon={SearchStatus}
                        visual="icon"
                        title="Nenhum guia encontrado"
                        description="Tente outras palavras ou abra um chamado no suporte para receber ajuda direta."
                    />
                ) : (
                    <div className="flex flex-col gap-2">
                        {results.map((article) => {
                            const topic = HELP_TOPICS.find((item) => item.id === article.topicId);

                            return (
                                <Link
                                    key={`${article.topicId}-${article.slug}`}
                                    to={`/help/${article.topicId}/${article.slug}`}
                                    className="group flex items-start gap-4 rounded-xl border border-transparent p-3 transition-[background-color,border-color] duration-150 ease-out hover:border-border/80 hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:p-4"
                                >
                                    <div className="min-w-0 flex-1 space-y-1">
                                        {topic && (
                                            <Text
                                                variant="label"
                                                className="uppercase tracking-[0.12em]"
                                            >
                                                {topic.title}
                                            </Text>
                                        )}
                                        <Heading as="h3" variant="card" className="text-brand">
                                            {article.title}
                                        </Heading>
                                        <Text variant="description">{article.description}</Text>
                                    </div>
                                    <ArrowRight className="mt-1.5 size-4 shrink-0 text-muted-foreground transition-[color,transform] duration-150 ease-out group-hover:translate-x-0.5 group-hover:text-foreground" />
                                </Link>
                            );
                        })}
                    </div>
                )}
            </PageSection>
        </PageShell>
    );
}
